import { HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse } from "@angular/common/http";
import { Inject, Injectable, Optional } from "@angular/core";
import { Observable, Subscriber, catchError, defer, finalize, from, map, of, share, switchMap, throwError } from "rxjs";
import { ApiErrorResponse } from "../models/images/api-error-response.interface";
import { FileImageServiceResponse } from "../models/images/file-image-storage-response.interface";
import { ImageFileResponse } from "../models/images/image-file-response.model";
import { createOstUploadOwnerId } from "../models/images/ost-upload-tracking.model";
import { IEnvironment } from "../models/interfaces/environment.interface";
import { OstResponse } from "../models/ost/ost-response.model";
import { ENVIRONMENT } from "../modules/keycloak.module";
import { UserStore } from "../stores/user.store";
import { HttpParamsUtil } from "../utils/http-params.util";
import { RetryHttpClient } from "./base/retryable-http.client";
import { OstUploadPersistenceService } from "./ost-upload-persistence.service";

@Injectable({ providedIn: "root" })
export class FileImageService extends HttpParamsUtil {
  private readonly inFlightImages = new Map<string, Observable<Blob>>();
  private readonly pendingImageSaves = new Map<string, Promise<void>>();

  constructor(
    private readonly httpClient: RetryHttpClient,
    private readonly ostUploadPersistenceService: OstUploadPersistenceService,
    @Inject(ENVIRONMENT) private readonly environment: IEnvironment,
    @Optional() private readonly userStore?: UserStore
  ) {
    super();
  }

  /** Display path: shares byte retrieval with prefetch; each subscriber gets its own object URL. */
  getImageByOstNumAndFileId(ost: OstResponse, fileId: string): Observable<string> {
    return this.getImageBytes(ost, fileId).pipe(
      map((blob: Blob) => URL.createObjectURL(blob)),
      catchError((error: unknown) => {
        console.error("Error fetching image:", error);
        return throwError(() => new Error("Failed to fetch image"));
      })
    );
  }

  /** Cache-only path: warms IndexedDB without allocating an object URL. */
  prefetchImageByOstNumAndFileId(ost: OstResponse, fileId: string): Observable<void> {
    return this.getImageBytes(ost, fileId).pipe(map(() => undefined));
  }

  /** Ref-counted single flight per OST+file; the last unsubscribe cancels HTTP and aborts the native download. */
  private getImageBytes(ost: OstResponse, fileId: string): Observable<Blob> {
    return defer(() => {
      const key = JSON.stringify([String(ost.ostNum), fileId]);
      const existing = this.inFlightImages.get(key);
      if (existing) return existing;
      const shared: Observable<Blob> = this.loadImageBytes(ost, fileId, key).pipe(
        finalize(() => {
          if (this.inFlightImages.get(key) === shared) this.inFlightImages.delete(key);
        }),
        share()
      );
      this.inFlightImages.set(key, shared);
      return shared;
    });
  }

  private loadImageBytes(ost: OstResponse, fileId: string, key: string): Observable<Blob> {
    // A save started by a torn-down operation may still be running; wait so it is read back instead of downloaded twice.
    const pendingSave = this.pendingImageSaves.get(key)?.catch(() => undefined);
    return from(Promise.all([createOstUploadOwnerId(this.userStore?.user() ?? {}), pendingSave])).pipe(
      switchMap(([ownerId]: [string | undefined, unknown]) =>
        from(ownerId ? this.ostUploadPersistenceService.getDownloadedImageCache(ownerId, ost.ostNum, fileId) : Promise.resolve(undefined)).pipe(
          switchMap((cached: Blob | undefined) => {
            if (cached) return of(cached);
            return this.downloadImage(ost, fileId).pipe(switchMap((file: File) => (ownerId ? this.saveImage(ownerId, ost, file, key) : of(file))));
          })
        )
      )
    );
  }

  private downloadImage(ost: OstResponse, fileId: string): Observable<File> {
    const url = `${this.environment.BASE_URL}${this.environment.FILE["BASE"]}${this.environment.FILE["IMAGES"]}/${ost.ostNum}/resized`;
    return this.fetchImage<Array<ImageFileResponse>>("POST", url, { body: [fileId] }).pipe(
      switchMap((response: Array<ImageFileResponse>) => {
        const imageUrl = response[0]?.url;
        if (!imageUrl) return throwError(() => new Error("Missing signed image URL"));
        return new Observable<File>((subscriber: Subscriber<File>) => {
          const controller = new AbortController();
          this.fetchImageAsFile(imageUrl, fileId, controller.signal).then(
            (file: File) => {
              subscriber.next(file);
              subscriber.complete();
            },
            (error: unknown) => subscriber.error(error)
          );
          return (): void => controller.abort();
        });
      })
    );
  }

  /** An IndexedDB write cannot be cancelled; it is tracked until it settles even if every subscriber leaves. */
  private saveImage(ownerId: string, ost: OstResponse, file: File, key: string): Observable<File> {
    const save = this.ostUploadPersistenceService.saveDownloadedImageCache(ownerId, ost.ostNum, file);
    this.pendingImageSaves.set(key, save);
    save
      .finally(() => {
        if (this.pendingImageSaves.get(key) === save) this.pendingImageSaves.delete(key);
      })
      .catch(() => undefined);
    return from(save).pipe(map(() => file));
  }

  private async fetchImageAsFile(url: string, name: string, signal?: AbortSignal): Promise<File> {
    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error(`Image download failed with status ${response.status}`);
    const blob = await response.blob();

    return new File([blob], name, {
      type: blob.type,
      lastModified: Date.now(),
    });
  }

  private fetchImage<T>(
    method: string,
    url: string,
    options?: {
      body?: object;
      headers?: HttpHeaders;
      params?:
        | HttpParams
        | {
            [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
          };
    }
  ): Observable<T> {
    return this.httpClient.request<object, T>(method, url, options);
  }

  getInfoBucketByOstNum(ostNum: string | number): Observable<FileImageServiceResponse> {
    return this.httpClient.get<FileImageServiceResponse>(`${this.environment.BASE_URL}${this.environment.FILE["BASE"]}${this.environment.FILE["STORAGE"]}/${ostNum}`);
  }

  /** Upload images without built-in retries; durable retry ownership belongs to OstUploadCoordinatorService. */
  uploadImages(ostNum: string | number, files: File[]): Observable<{ status: number }> {
    const formData = new FormData();
    formData.append("ostNum", String(ostNum));

    files.forEach((file: File) => {
      formData.append("files", file, file.name);
    });

    return this.httpClient
      .postResponseWithoutRetry<FormData, void>(`${this.environment.BASE_URL}${this.environment.FILE["BASE"]}${this.environment.FILE["UPLOAD"]}`, formData, {
        observe: "response",
      })
      .pipe(
        map((response: HttpResponse<void>) => ({ status: response.status })),
        catchError((err: HttpErrorResponse) => {
          throw err.error as ApiErrorResponse;
        })
      );
  }

  getMailLabel(ostNum: string | number): Observable<ImageFileResponse> {
    /*        //example ::
        this.fileService.getMailLabel("1").subscribe((file: ImageFileResponse) => {
            console.log(file);
        });*/
    return this.httpClient.get<ImageFileResponse>(`${this.environment.BASE_URL}${this.environment.FILE["BASE"]}${this.environment.FILE["MAIL_LABEL"]}/${ostNum}`);
  }
}
